<template>
  <div>
    <search-form
      :fields="[
        'term',
        'sender_type',
        'private_message',
        'inbox',
        'date_range',
      ]"
      :exclude-today="isAccountTypeParent"
      @search="onSearch"
    />
    <div class="margin-top-2" v-if="showResult" tab="messages">
      <section class="search-tab-conversation-table-wrap">
        <woot-tabs v-if="tabs.length > 0" :index="activeTabIndex" :border="false" @change="onTabChange" class="margin-bottom-1">
          <woot-tabs-item
            v-for="item in tabs"
            :key="item.key"
            :name="item.name"
            :showBadge="false"
          />
        </woot-tabs>
        <template v-if="activeTabIndex === 0">
          <search-summary-table
            :summary="summary"
            :is-fetching="uiFlags.message.isFetchingSummary"
          />
        </template>
        <template v-else>
          <ve-table
            :fixed-header="false"
            max-height="calc(100vh - 11.4rem)"          
            :columns="columns"
            :table-data="tableData"
            :border-around="false"         
          />
        
          <empty-state
            v-if="!uiFlags.message.isFetching && !messageRecords.length"
            :title="$t('SEARCH.EMPTY_STATE')"
          />
          <div v-if="uiFlags.message.isFetching" class="loader">
            <spinner />
            <span>{{ $t('SEARCH.SEARCHING_STATE') }}</span>
          </div>
        </template>
      </section>
      
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { format } from 'date-fns';
import { frontendURL } from 'dashboard/helper/URLHelper';
import timeMixin from 'dashboard/mixins/time';
import alertMixin from 'shared/mixins/alertMixin';

import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import MessageContent from './MessageContent';
import SearchForm from './SearchForm';
import SearchSummaryTable from './SearchSummaryTable';


export default {
  mixins: [timeMixin, alertMixin],
  components: {
    VeTable,
    Spinner,
    EmptyState,    
    MessageContent,
    SearchForm,
    SearchSummaryTable,
  },
  data() {
    return {
      showResult: false,      
      termMessage: '',
      activeTabIndex: 1
    };
  },  
  computed: {
    ...mapGetters({
      messageRecords: 'conversationSearch/getMessageRecords',
      uiFlags: 'conversationSearch/getUIFlags',
      isAccountTypeParent: 'isAccountTypeParent',
      summary: 'conversationSearch/getMessageSummaryRecords',
    }),
    tableData() {
      if (this.uiFlags.message.isFetching) {
        return [];
      }
      return this.messageRecords.map(item => {
        const accountId = item.account_id || this.$route.params.accountId;
        return {
          ...item,
          link: frontendURL(
            `accounts/${accountId}/conversations/${item.conversation_id}?messageId=${item.id}`
          ),
        };
      });
    },
    columns() {
      const columns = [
        {
          field: 'id',
          key: 'id',
          title: "#",
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 90,
          renderBodyCell: ({ row }) => {
            const isOtherAccount =
              row.account_id &&
              String(this.$route.params.accountId) !== String(row.account_id);
            if (isOtherAccount) {
              return <a href={row.link}>{row.id}</a>;
            }
            return (
              <router-link to={row.link} class="user-name">
                {row.id}
              </router-link>
            );
          },
        },
        {
          field: 'created_at',
          key: 'created_at',
          title: this.$t("SEARCH.LIST.HEADER.SEND_AT"),
          align: this.isRTLView ? 'right' : 'left',          
          width: 150,
          renderBodyCell: ({ row }) => {            
            return this.messageStamp(
              new Date(row.created_at),
              'dd/MM/yy - HH:mm'
            );
          },
        },
        {
          field: 'sender_by',
          key: 'sender_by',          
          title: this.$t("SEARCH.LIST.HEADER.SENDER_BY"),
          align: this.isRTLView ? 'right' : 'left',
          width: 260,
          renderBodyCell: ({ row }) => {
            let text = (row.sender_type === 'User' ? this.$t("SEARCH.LIST.HEADER.USER") : this.$t("SEARCH.LIST.HEADER.CLIENT")) + ': ';
            text += (row.sender ? row.sender.name : '---');
            return text;
          },  
        },
        {
          field: 'content',
          key: 'content',
          title: this.$t("SEARCH.LIST.HEADER.MESSAGE_CONTENT"),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          renderBodyCell: ({ row }) => (
            <message-content              
              content={row.content}
              search-term={this.termMessage}
            >
              {row.id}
            </message-content>       
          ),          
        },
        {
          field: 'inbox',
          key: 'inbox',          
          title: this.$t("SEARCH.LIST.HEADER.INBOX"),
          align: this.isRTLView ? 'right' : 'left',
          width: 260,
          renderBodyCell: ({ row }) => {            
            return (row.inbox ? row.inbox.name : '---');
          },
        },
      ];
      if (this.isAccountTypeParent) {
        columns.splice(1, 0, {
          field: 'account',
          key: 'account',
          title: this.$t('SEARCH.LIST.HEADER.ACCOUNT'),
          align: this.isRTLView ? 'right' : 'left',
          width: 200,
          renderBodyCell: ({ row }) => row.account_name ?? '---',
        });
      }
      return columns;
    },    
    tabs() {
      if(this.isAccountTypeParent){
        return [
          {
            key: 'summary',
            name: this.$t('SEARCH.TABS.SUMMARY'),
          },
          {
            key: 'results',
            name: this.$t('SEARCH.TABS.RESULTS'),
          },
        ]
      }
      return [];
    }
  },
  methods: {
    onSearch(data) {
      this.showResult = data !== null;
      if(!data || Object.keys(data).length == 0){
        this.$store.dispatch('conversationSearch/clearSearchResults', { type: 'message' });
        return;
      }
      this.termMessage = data.term;
      const searchParams = {
        term: data.term,
        sender_type: data.sender_type ? data.sender_type.id : null,
        private_message: data.private_message
          ? data.private_message.id
          : null,
        inbox: data.inbox ? data.inbox.id : null,
        subaccount: data.subaccount ? data.subaccount.id : null,
        start_date: format(data.tempDateRange[0], 'yyyy-MM-dd'),
        end_date: format(data.tempDateRange[1], 'yyyy-MM-dd'),
      };
      const requests = [
        this.$store.dispatch('conversationSearch/messageSearch', searchParams),
      ];
      // Na conta mãe, o resumo por subconta é carregado junto com os resultados
      if (this.isAccountTypeParent) {
        requests.push(
          this.$store.dispatch(
            'conversationSearch/messageSearchSummary',
            searchParams
          )
        );
      }
      // Promise.all: as duas requisições falham pelas mesmas validações, então o alerta aparece uma vez só
      Promise.all(requests).catch(error => {
        const message = error?.response?.data?.error;
        if (message) {
          this.showAlert(message);
        }
      });
    },
    onClickConversation(conversationId) {
      this.$router.push(`/app/accounts/${this.$route.params.accountId}/conversations/${conversationId}`);      
    },
    onTabChange(index) {
      this.activeTabIndex = index;
    },
  },
};
</script>
<style lang="scss" scoped> 
  .search-tab-conversation-table-wrap{
    .loader {
      align-items: center;
      display: flex;
      font-size: var(--font-size-default);
      justify-content: center;
      padding: var(--space-big);
    }
  }
</style>

